import { useMemo } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';

import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { selectors, useAppSelector } from 'state/global-state';
import { IParsedLoyaltyReward, RewardRedemptionMethod } from 'state/loyalty/hooks/types';

export interface IAppliedReward {
  rewardId: string;
  redemptionMethod: RewardRedemptionMethod;
  cartId: string;
  loyaltyEngineId?: string;
  rewardBenefitId?: string;
}

const mapSliceRewardToApplied = (
  appliedReward: IParsedLoyaltyReward
): IAppliedReward | undefined => {
  if (!appliedReward.sanityId) {
    return undefined;
  }
  return {
    rewardId: appliedReward.sanityId,
    redemptionMethod: appliedReward.redemptionMethod,
    cartId: appliedReward.cartId,
    loyaltyEngineId: appliedReward.rewardId,
    rewardBenefitId: appliedReward.rewardBenefitId,
  };
};

/**
 * All applied loyalty rewards (Redux plus minimal-cart fallback for 1M never-hydrated edge case).
 */
export const useAppliedRewards = (): IAppliedReward[] => {
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedRewardsParsed);
  const { getAppliedRewards } = useMinimalCart();

  return useMemo(() => {
    if (!isUnifiedMenuEnabled) {
      return (appliedRewards ?? [])
        .map(mapSliceRewardToApplied)
        .filter((r): r is IAppliedReward => r !== undefined);
    }

    if (appliedRewards?.length) {
      return appliedRewards
        .map(mapSliceRewardToApplied)
        .filter((r): r is IAppliedReward => r !== undefined);
    }

    const minimalRows = getAppliedRewards();
    if (minimalRows.length) {
      return minimalRows
        .filter(row => row.rewardId && row.cartId)
        .map(row => ({
          rewardId: row.rewardId ?? '',
          redemptionMethod: RewardRedemptionMethod.MOBILE,
          cartId: row.cartId ?? '',
        }));
    }

    return [];
  }, [appliedRewards, getAppliedRewards, isUnifiedMenuEnabled]);
};

/**
 * First applied reward only. Prefer {@link useAppliedRewards} when the cart may hold more than one reward.
 */
export const useAppliedReward = (): IAppliedReward | undefined => {
  const applied = useAppliedRewards();
  return applied[0];
};
